import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Box,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Sensors as SensorsIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/authContext';
import AddPayloadDialog from '../../components/fleet/AddPayloadDialog';
import EditPayloadDialog from '../../components/fleet/EditPayloadDialog';
import { useToast } from '../../hooks/useToast';
import styles from '../../styles/PayloadInventory.module.css';

const PayloadInventory = () => {
  const { currentOrganization, userRole } = useOrganization();
  const { token } = useAuth();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [payloads, setPayloads] = useState([]);
  const [filteredPayloads, setFilteredPayloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);

  // Permission checks
  const canManagePayloads = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());
  const canDeletePayloads = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch payloads from backend
  useEffect(() => {
    if (currentOrganization?.organizationId) {
      fetchPayloads();
    }
  }, [currentOrganization]);

  const fetchPayloads = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/payloads`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch payloads');
      }

      const data = await response.json();
      const fetchedPayloads = data.payloads || [];
      setPayloads(fetchedPayloads);
      setFilteredPayloads(fetchedPayloads);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPayloads(payloads);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = payloads.filter(
        (payload) =>
          payload.type?.toLowerCase().includes(query) ||
          payload.manufacturer?.toLowerCase().includes(query) ||
          payload.model?.toLowerCase().includes(query) ||
          payload.serialNumber?.toLowerCase().includes(query) ||
          payload.nickname?.toLowerCase().includes(query) ||
          payload.status?.toLowerCase().includes(query)
      );
      setFilteredPayloads(filtered);
    }
  }, [searchQuery, payloads]);

  const handleAddPayload = async (payloadData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/payloads`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payloadData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add payload');
      }

      await fetchPayloads();
      setAddDialogOpen(false);
      showSuccess('Payload added successfully!');
    } catch (err) {
      showError(`Failed to add payload: ${err.message}`);
      throw err;
    }
  };

  const handleEditPayload = async (payloadId, payloadData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/payloads/${payloadId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payloadData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update payload');
      }

      await fetchPayloads();
      setEditDialogOpen(false);
      setSelectedPayload(null);
      showSuccess('Payload updated successfully!');
    } catch (err) {
      showError(`Failed to update payload: ${err.message}`);
      throw err;
    }
  };

  const handleDeletePayload = async (payloadId) => {
    if (!window.confirm('Are you sure you want to delete this payload?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/payloads/${payloadId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete payload');
      }

      await fetchPayloads();
      showSuccess('Payload deleted successfully!');
    } catch (err) {
      showError(`Failed to delete payload: ${err.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'retired':
        return 'default';
      case 'damaged':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeColor = (type) => {
    const typeColors = {
      camera: 'primary',
      lidar: 'secondary',
      multispectral: 'success',
      thermal: 'error',
      delivery: 'info',
      sprayer: 'warning'
    };
    return typeColors[type?.toLowerCase()] || 'default';
  };

  // Calculate statistics
  const maxPayloads = currentOrganization?.subscription?.limits?.maxPayloads || 0;
  const currentCount = payloads.length;
  const activeCount = payloads.filter(p => p.status === 'active').length;
  const maintenanceCount = payloads.filter(p => p.status === 'maintenance').length;

  // Count by type
  const typeStats = payloads.reduce((acc, payload) => {
    const type = payload.type || 'other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const atLimit = maxPayloads > 0 && currentCount >= maxPayloads;

  if (loading) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Typography>Loading payload inventory...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Payload & Sensor Inventory
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization's sensors, cameras, and equipment
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} className={styles.statsGrid}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Payloads
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {currentCount} / {maxPayloads}
              </Typography>
              {atLimit && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                  Limit reached
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Active
              </Typography>
              <Typography variant="h3" color="success.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Maintenance
              </Typography>
              <Typography variant="h3" color="warning.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {maintenanceCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Types
              </Typography>
              <Typography variant="h3" color="info.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {Object.keys(typeStats).length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                Different types
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Add Button */}
      <Box
        className={styles.searchBar}
        sx={{
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}
      >
        <TextField
          placeholder="Search payloads by type, manufacturer, model, serial number..."
          variant="outlined"
          fullWidth={isMobile}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        {canManagePayloads && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={atLimit}
            fullWidth={isMobile}
          >
            Add Payload
          </Button>
        )}
      </Box>

      {/* Payload Table/Cards */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ mt: 3 }}>
          {filteredPayloads.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {searchQuery
                    ? 'No payloads found matching your search'
                    : 'No payloads in your fleet yet. Add your first payload to get started!'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filteredPayloads.map((payload) => (
                <Card key={payload.id} variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Compact Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {payload.nickname || payload.model}
                        </Typography>
                        {payload.nickname && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {payload.model}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                        <Chip
                          label={formatStatus(payload.type)}
                          color={getTypeColor(payload.type)}
                          size="small"
                          icon={<SensorsIcon />}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={formatStatus(payload.status)}
                          color={getStatusColor(payload.status)}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>

                    {/* Compact Details - 3 columns */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Manufacturer
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {payload.manufacturer}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Weight
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {payload.weight} g
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Calibration
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {formatDate(payload.lastCalibrationDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  {/* Actions */}
                  {canManagePayloads && (
                    <CardActions sx={{ p: 1, pt: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedPayload(payload);
                          setEditDialogOpen(true);
                        }}
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {canDeletePayloads && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePayload(payload.id)}
                          sx={{ padding: '4px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Nickname / Model</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Calibration</TableCell>
                <TableCell>Compatible Drones</TableCell>
                {canManagePayloads && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayloads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManagePayloads ? 9 : 8} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery
                        ? 'No payloads found matching your search'
                        : 'No payloads in your fleet yet. Add your first payload to get started!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayloads.map((payload) => (
                  <TableRow key={payload.id}>
                    <TableCell>
                      <Chip
                        label={formatStatus(payload.type)}
                        color={getTypeColor(payload.type)}
                        size="small"
                        icon={<SensorsIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {payload.nickname || payload.model}
                        </Typography>
                        {payload.nickname && (
                          <Typography variant="caption" color="text.secondary">
                            {payload.model}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{payload.manufacturer}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {payload.serialNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{payload.weight} g</TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatus(payload.status)}
                        color={getStatusColor(payload.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(payload.lastCalibrationDate)}</TableCell>
                    <TableCell>
                      {payload.compatibleDroneModels ? (
                        <Tooltip title={payload.compatibleDroneModels}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {payload.compatibleDroneModels}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    {canManagePayloads && (
                      <TableCell align="right">
                        <Tooltip title="Edit payload">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedPayload(payload);
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canDeletePayloads && (
                          <Tooltip title="Delete payload">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePayload(payload.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Payload Dialog */}
      {addDialogOpen && (
        <AddPayloadDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAddPayload}
        />
      )}

      {/* Edit Payload Dialog */}
      {editDialogOpen && selectedPayload && (
        <EditPayloadDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedPayload(null);
          }}
          onEdit={handleEditPayload}
          payload={selectedPayload}
        />
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PayloadInventory;
